import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        info: 'border-transparent bg-[#FF5B2766] text-jen-orange hover:bg-jen-orange/20',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        noBackground: 'border-transparent text-foreground',
        success: 'border-transparent bg-green-500 text-white hover:bg-green-600',
      },
      size: {
        mini: 'text-[0.8rem] py-0.5 px-1',
        small: 'text-xs py-0.5 px-2',
        medium: 'text-sm py-1 px-3',
        large: 'text-lg py-1.5 px-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  style?: React.CSSProperties;
  noHover?: boolean;
}

function Badge({ className, variant, size, style, noHover, ...props }: BadgeProps) {
  const baseClasses = badgeVariants({ variant, size });
  const classes = noHover ? baseClasses.replace(/hover:[^\s]+/g, '') : baseClasses;
  return <div className={cn(classes, className)} style={style} {...props} />;
}

export { Badge, badgeVariants };
